import { useLayoutEffect, useState } from "react";

import { BoxPosition, UseBoxPositionParams } from "./types";

// In each option: the element holding the box and the text, then the text itself.
const OPTION_CONTENT_SELECTOR = "[data-option-content]";
const OPTION_TEXT_SELECTOR = "[data-option-text]";

// Width inside the borders and the paddings. Not clientWidth, which is rounded to the pixel: a
// text just at the limit would then be said to fit.
function getInnerWidth(element: HTMLElement): number {
    const style = getComputedStyle(element);

    return (
        element.getBoundingClientRect().width -
        parseFloat(style.borderLeftWidth) -
        parseFloat(style.borderRightWidth) -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight)
    );
}

// Width of the text written on a single line, wherever the box currently is. The text is
// stretched for the time of the measure only, and restored before the screen is drawn.
function getOneLineTextWidth(text: HTMLElement): number {
    text.style.flex = "none";
    text.style.maxWidth = "none";
    text.style.width = "max-content";
    const width = text.getBoundingClientRect().width;
    text.style.flex = "";
    text.style.maxWidth = "";
    text.style.width = "";

    return width;
}

// Width taken next to the text: the box (and the picture of a candidate), with their gaps.
function getWidthNextToText(content: HTMLElement, text: HTMLElement): number {
    const elementsNextToText = Array.from(content.children).filter(
        (element) => element !== text,
    );
    const gap = parseFloat(getComputedStyle(content).columnGap) || 0;

    return elementsNextToText.reduce(
        (width, element) => width + element.getBoundingClientRect().width + gap,
        0,
    );
}

function doesTextFitNextToBox(content: HTMLElement): boolean {
    const text = content.querySelector<HTMLElement>(OPTION_TEXT_SELECTOR);

    if (!text) {
        return true;
    }

    const neededWidth =
        getWidthNextToText(content, text) + getOneLineTextWidth(text);

    return neededWidth <= getInnerWidth(content);
}

// The same position for every option of the line, so that the boxes stay aligned.
function getBoxPosition(contents: HTMLElement[]): BoxPosition {
    const allTextsFit = contents.every(doesTextFitNextToBox);

    return allTextsFit ? "side" : "top";
}

// Options side by side: the box stays next to the text, as in the mockup, until one text of the
// line no longer fits there. The box then goes on top of the text, in every option of the line.
// The text is always measured on a single line, so the result is the same whatever the current
// position, and the options do not switch back and forth.
export function useBoxPosition({
    containerRef,
    isSideBySide,
    optionCount,
}: UseBoxPositionParams): BoxPosition {
    const [boxPosition, setBoxPosition] = useState<BoxPosition>("side");

    // Before the screen is drawn, so that the boxes never show next to the texts first.
    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!isSideBySide || !container) {
            setBoxPosition("side");
            return;
        }

        const contents = Array.from(
            container.querySelectorAll<HTMLElement>(OPTION_CONTENT_SELECTOR),
        );

        function updateBoxPosition() {
            setBoxPosition(getBoxPosition(contents));
        }

        updateBoxPosition();

        // Missing without a real browser, as in the tests (jsdom).
        if ("undefined" === typeof ResizeObserver) {
            return;
        }

        // The texts are watched too: a selected option is bold, so its text gets wider.
        const resizeObserver = new ResizeObserver(updateBoxPosition);
        contents.forEach((content) => {
            resizeObserver.observe(content);
            const text = content.querySelector(OPTION_TEXT_SELECTOR);

            if (text) {
                resizeObserver.observe(text);
            }
        });

        return () => {
            resizeObserver.disconnect();
        };
    }, [containerRef, isSideBySide, optionCount]);

    return boxPosition;
}
